import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Button, Dropdown, Empty, List, Typography, Space, Tag, Modal, notification } from 'antd';
import { BellOutlined, UserOutlined, ClockCircleOutlined, CheckOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { listenToHelpRequests, markNotificationAsRead, getUnreadNotificationCount } from './NotificationSystem';

const { Text } = Typography;

const NotificationBell = () => {
  const [helpRequests, setHelpRequests] = useState([]);
  const [processedNotifications, setProcessedNotifications] = useState(new Set());

  const [api, contextHolder] = notification.useNotification();
  const todosRef = useRef([]);

  // Load todos from localStorage on mount
  useEffect(() => {
    const savedTodos = localStorage.getItem('superadmin-todos');
    if (savedTodos) {
      const parsedTodos = JSON.parse(savedTodos);
      todosRef.current = parsedTodos;
    }
  }, []);

  // Set up real-time listener for help requests
  useEffect(() => {
    const unsubscribe = listenToHelpRequests((requests) => {
      setHelpRequests(requests.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    });

    return () => unsubscribe();
  }, []);

  const handleMarkAsRead = useCallback(async (request) => {
    try {
      await markNotificationAsRead('help_requests', request.key, request.dateKey);
      api.success({
        message: 'Marked as Read',
        description: 'Notification has been deleted.',
      });
    } catch (error) {
      console.error('Failed to mark as read:', error);
      api.error({
        message: 'Error',
        description: 'Failed to mark as read.',
      });
    }
  }, [api]);

  const handleAddToTodos = useCallback(async (request) => {
    // Check if this notification has already been processed
    const notificationKey = `${request.dateKey}-${request.key}`;
    if (processedNotifications.has(notificationKey)) {
      api.warning({
        message: 'Already Added',
        description: 'This help request is already in your todo list.',
      });
      return;
    }

    try {
      const newTodo = {
        id: `todo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title: `Help request from ${request.userName}`,
        description: `${request.userRole} at ${request.facilityId}: ${request.message}`,
        createdAt: new Date().toISOString(),
        status: 'pending',
        source: 'notification',
        notificationKey
      };

      const updatedTodos = [...todosRef.current, newTodo];
      todosRef.current = updatedTodos;
      localStorage.setItem('superadmin-todos', JSON.stringify(updatedTodos));

      // Mark this notification as processed
      setProcessedNotifications(prev => new Set([...prev, notificationKey]));

      // Remove the notification from Firebase
      await markNotificationAsRead('help_requests', request.key, request.dateKey);

      // Dispatch custom event to notify Super Admin component
      window.dispatchEvent(new CustomEvent('todosUpdated', { 
        detail: { todos: updatedTodos } 
      }));

      api.success({
        message: 'Added to Todos',
        description: 'Help request has been added to your todo list and removed from notifications.',
      });
    } catch (error) {
      console.error('Error adding to todos:', error);
      api.error({
        message: 'Error',
        description: 'Failed to add to todos. Please try again.',
      });
    }
  }, [api, processedNotifications]);

  const handleIgnore = (request) => {
    Modal.confirm({
      title: 'Ignore Notification',
      content: 'This will leave the notification unattended. Are you sure?',
      okText: 'Yes, Ignore',
      cancelText: 'Cancel',
      onOk: () => {
        api.info({
          message: 'Notification Ignored',
          description: 'The notification will remain in the list unattended.',
        });
      }
    });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    try {
      const now = new Date();
      const notificationTime = new Date(timestamp);
      
      if (isNaN(notificationTime.getTime())) return 'Invalid date';
      
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Unknown time';
    }
  };

  const unreadCount = getUnreadNotificationCount(helpRequests);

  const dropdownContent = (
    <div style={{ 
      width: 350, 
      maxHeight: 400, 
      overflowY: 'auto',
      backgroundColor: 'var(--surface-primary)',
      border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-medium)'
    }}>
      <div style={{ 
        padding: 'var(--spacing-md)', 
        borderBottom: '1px solid var(--border-primary)',
        backgroundColor: 'var(--surface-secondary)'
      }}>
        <Text strong style={{ color: 'var(--text-primary)' }}>
          Help Requests ({unreadCount} unread)
        </Text>
      </div>
      
      {helpRequests.length === 0 ? (
        <div style={{ padding: 'var(--spacing-lg)' }}>
          <Empty 
            description="No help requests"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <List
          dataSource={helpRequests}
          renderItem={(request) => (
            <List.Item
              style={{ 
                padding: 'var(--spacing-md)',
                backgroundColor: request.status === 'unread' ? 'rgba(24, 144, 255, 0.05)' : 'transparent',
                borderBottom: '1px solid var(--border-secondary)'
              }}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddToTodos(request);
                  }}
                  title="Add to Todos"
                />,
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleIgnore(request);
                  }}
                  title="Ignore"
                />,
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead(request);
                  }}
                  title="Mark as Read"
                />
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ position: 'relative' }}>
                    <UserOutlined 
                      style={{ 
                        fontSize: 24, 
                        color: 'var(--primary-color)',
                        backgroundColor: 'rgba(24, 144, 255, 0.1)',
                        padding: 8,
                        borderRadius: '50%'
                      }} 
                    />
                    {request.status === 'unread' && (
                      <div style={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 10,
                        height: 10,
                        backgroundColor: 'var(--error-color)',
                        borderRadius: '50%',
                        border: '2px solid var(--surface-primary)'
                      }} />
                    )}
                  </div>
                }
                title={
                  <Space direction="vertical" size={4}>
                    <Text strong style={{ color: 'var(--text-primary)' }}>
                      {request.userName}
                    </Text>
                    <Space>
                      <Tag color="blue" size="small">{request.userRole}</Tag>
                      <Tag color="green" size="small">{request.facilityId}</Tag>
                    </Space>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <Text style={{ color: 'var(--text-secondary)' }}>
                      {request.message}
                    </Text>
                    <Space>
                      <ClockCircleOutlined style={{ color: 'var(--text-tertiary)' }} />
                      <Text style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
                        {formatTimeAgo(request.createdAt)}
                      </Text>
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <>
      {contextHolder}
      <Dropdown
        dropdownRender={() => dropdownContent}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button 
          type="text" 
          style={{ 
            position: 'relative',
            color: 'var(--text-primary)',
            marginRight: 'var(--spacing-sm)'
          }}
        >
          <Badge count={unreadCount} size="small">
            <BellOutlined style={{ fontSize: 20 }} />
          </Badge>
        </Button>
      </Dropdown>
    </>
  );
};

export default NotificationBell;
